import React from "react";
import { Link } from "react-router-dom";
import { ASSETS, VENTURES, SOCIAL, CONTACT_EMAILS, PODCASTS, HANDLE } from "@/lib/brand";
import SocialIcon from "@/components/SocialIcon";
import Newsletter from "@/components/Newsletter";
import Pillars from "@/components/Pillars";

const YEAR = new Date().getFullYear();

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-navy border-t hairline pt-24 pb-12 mt-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Closing signature flourish */}
        <div className="flex flex-col items-center text-center mb-16" data-testid="footer-signature">
          <p className="font-overline text-silver-muted mb-6">— Office of</p>
          <img
            src={ASSETS.signature}
            alt="Miguel Santana signature"
            className="signature-img w-64 md:w-96 h-auto"
          />
          <p className="mt-6 font-overline text-silver-muted tracking-[0.3em]">Miguel Santana</p>
        </div>

        {/* The ThrEEE's — compact band */}
        <div className="border-y hairline py-8 mb-16" data-testid="footer-pillars">
          <Pillars variant="compact" />
        </div>

        {/* Newsletter band */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start pb-16 border-y hairline py-16" data-testid="footer-newsletter">
          <div className="col-span-12 md:col-span-6">
            <p className="font-overline text-champagne mb-4">— The Dispatch</p>
            <h3 className="font-display text-3xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
              Join the<br />
              <span className="italic font-light">private list.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-6">
            <p className="font-body text-silver-muted leading-relaxed mb-6">
              A short, occasional newsletter from the Office of Miguel Santana — new ventures,
              podcast drops, books in development, and press features. Unsubscribe any time.
            </p>
            <Newsletter source="footer" />
          </div>
        </div>

        {/* Main sitemap */}
        <div className="grid grid-cols-12 gap-12 pt-20 pb-16">
          <div className="col-span-12 md:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <img src={ASSETS.monogram} alt="MS" className="h-12 w-12 rounded-sm border hairline" />
              <div>
                <p className="font-overline text-silver-muted">Office of</p>
                <p className="font-display text-xl text-silver">Miguel Santana</p>
              </div>
            </Link>
            <p className="mt-6 max-w-md font-body text-silver-muted text-sm leading-relaxed">
              Dominican-American entrepreneur, founder, and chief executive of MPR Ventures —
              a multi-city American conglomerate operating across media, brands, capital,
              culture, sports, and civic impact.
            </p>

            <div className="mt-8">
              <p className="font-overline text-silver-muted mb-3">All handles</p>
              <p className="font-display text-2xl text-champagne tracking-wide">{HANDLE}</p>
            </div>
          </div>

          <div className="col-span-6 md:col-span-2">
            <p className="font-overline text-silver-muted mb-5">Site</p>
            <ul className="space-y-3">
              {["/", "/about", "/ventures", "/projects", "/journal", "/press", "/contact"].map((to, i) => (
                <li key={to}>
                  <Link to={to} className="font-body text-silver hover:text-champagne transition-colors duration-300">
                    {["Index", "About", "Ventures", "Projects", "Journal", "Press Kit", "Contact"][i]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-3">
            <p className="font-overline text-silver-muted mb-5">The Conglomerate</p>
            <ul className="space-y-3">
              {VENTURES.map((v) => (
                <li key={v.id}>
                  <Link to={`/ventures#${v.id}`} className="block font-body text-silver hover:text-champagne transition-colors duration-300">
                    <span>{v.name}</span>
                    <span className="font-overline text-silver-muted text-[0.65rem] block mt-0.5">{v.city}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-12 md:col-span-3">
            <p className="font-overline text-silver-muted mb-5">Listen</p>
            <ul className="space-y-3">
              {PODCASTS.map((p) => (
                <li key={p.name}>
                  <Link to="/projects" className="font-body text-silver hover:text-champagne transition-colors duration-300">{p.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact buckets + Social */}
        <div className="grid grid-cols-12 gap-12 pt-16 border-t hairline">
          <div className="col-span-12 md:col-span-6">
            <p className="font-overline text-champagne mb-5">— Contact</p>
            <ul className="space-y-3" data-testid="footer-contact-emails">
              {CONTACT_EMAILS.map((c) => (
                <li key={c.value} className="grid grid-cols-12 gap-4 items-baseline">
                  <span className="col-span-12 md:col-span-5 font-overline text-silver-muted">{c.label}</span>
                  <a
                    href={`mailto:${c.value}`}
                    data-testid={`footer-email-${c.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                    className="col-span-12 md:col-span-7 font-body text-silver hover:text-champagne transition-colors"
                  >
                    {c.value}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/contact" data-testid="footer-book-cta" className="btn-editorial btn-editorial-filled">
                Book / Inquire
              </Link>
            </div>
          </div>

          <div className="col-span-12 md:col-span-6">
            <p className="font-overline text-champagne mb-5">— Follow {HANDLE}</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="footer-social">
              {SOCIAL.map((s) => (
                <SocialIcon
                  key={s.name}
                  icon={s.icon}
                  label={s.name}
                  href={s.url}
                  testId={`social-${s.icon}`}
                />
              ))}
            </div>
            <p className="mt-6 font-body text-silver-muted text-sm">
              Instagram · X · TikTok · YouTube · Spotify · Apple Podcasts
            </p>

            <div className="mt-10">
              <p className="font-overline text-silver-muted mb-3">Press Kit</p>
              <Link to="/press" data-testid="footer-press-kit" className="font-body text-silver hover:text-champagne transition-colors">
                Open the Press Kit & Downloads →
              </Link>
            </div>
          </div>
        </div>

        {/* Wordmark band */}
        <div className="border-t hairline pt-16 mt-16">
          <p
            className="font-display text-[14vw] md:text-[10vw] leading-none tracking-tighter text-silver/10 select-none"
            aria-hidden="true"
          >
            iammiguelsantana
          </p>
        </div>

        {/* Legal verbatim */}
        <div className="mt-12 border-t hairline pt-10" data-testid="footer-legal">
          <p className="font-overline text-champagne mb-6">— Legal Notice</p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 text-[0.78rem] leading-[1.75] font-body text-silver-muted">
            <div className="md:col-span-6 space-y-4">
              <p>
                <span className="text-silver">© {YEAR} Miguel Santana &amp; MPR Ventures, LLC.</span>{" "}
                All rights reserved. <em>iammiguelsantana.com</em> and all related content,
                photography, copy, marks, and creative assets are the exclusive property of
                Miguel Santana and MPR Ventures, LLC. No portion of this website may be reproduced,
                distributed, transmitted, scraped, framed, or otherwise exploited in any form or by
                any means — electronic, mechanical, or otherwise — including data-mining, AI
                training, photocopying, recording, or any information storage and retrieval system,
                without the express prior written permission of MPR Ventures, LLC.
              </p>
              <p>
                <span className="text-silver">Trademarks.</span> "Miguel Santana", the Miguel
                Santana signature, the <em>MS</em> monogram, "MPR Ventures", "The ThrEEE's to Success", and the names and
                marks of Court Street Group, Pacific Standard Brands, Skyline Global, Santana
                Enterprises, the Better Tomorrow Initiative, TruFlights, Fresh Start Credit,
                Skyline, Skyline TV, Standard Society, Athlete City, "Same World", "Built
                Different", Raise The Standard, Dating Rx, The Voice of Reason, and Miguel Talks
                Sports are trademarks, service marks, trade dress, and/or registered marks of
                MPR Ventures, LLC and its affiliates. All other trademarks referenced are the
                property of their respective owners and are used for identification purposes only.
              </p>
            </div>
            <div className="md:col-span-6 space-y-4">
              <p>
                <span className="text-silver">Editorial &amp; Press.</span> Editorial features,
                press quotes, third-party publication marks (including but not limited to Forbes,
                GQ, The New York Times, Bloomberg, Rolling Stone, Esquire, Complex, The Breakfast
                Club, Men's Health, Variety, Fast Company, and others displayed in the Journal or
                "Featured In" section) appear for press-record purposes only and remain the
                property of their respective publishers. Inclusion does not imply endorsement by
                such publishers.
              </p>
              <p>
                <span className="text-silver">No Offer; No Advice.</span> Nothing on this website
                constitutes an offer, solicitation, or recommendation to buy, sell, or invest in
                any security, partnership interest, fund, product, or service of MPR Ventures, LLC
                or any of its affiliates. Information herein is provided for general informational
                purposes only and does not constitute legal, tax, financial, or investment advice.
              </p>
              <p>
                <span className="text-silver">Privacy.</span> Inquiries submitted via the contact
                form and newsletter signups are received by the Office of Miguel Santana and used
                solely to respond to your request or send occasional updates. We do not sell,
                rent, or share your information with third parties for marketing purposes.
              </p>
              <p>
                <span className="text-silver">Jurisdiction.</span> This website is operated from
                the United States. Any disputes arising out of or relating to the use of this
                website shall be governed by the laws of the State of New York, without regard to
                its conflict-of-law principles.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t hairline pt-8">
          <p className="font-overline text-silver-muted">
            © {YEAR} MPR Ventures, LLC · All Rights Reserved · New York · Las Vegas · Los Angeles · Miami · Washington DC
          </p>
          <div className="flex items-center gap-6 flex-wrap">
            <Link to="/admin/login" data-testid="footer-admin-link" className="font-overline text-silver-muted hover:text-silver transition-colors">
              Admin
            </Link>
            <a href="mailto:hello@iammiguelsantana.com" className="font-overline text-silver-muted hover:text-silver transition-colors">
              hello@iammiguelsantana.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
